import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Copy, Loader2 } from 'lucide-react';

import { toolService } from '../../services';
import { env } from '../../config/env';
import { toolToast } from '../../utils/toolToast';

interface ApiDocsPanelProps {
  toolId: string;
}

type OpenApiDocument = Record<string, unknown> & {
  info?: { title?: string; description?: string; version?: string };
  paths?: Record<string, Record<string, OpenApiOperation>>;
  components?: {
    schemas?: Record<string, OpenApiSchema>;
    securitySchemes?: Record<string, unknown>;
  };
};

type OpenApiOperation = {
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: Array<{
    name: string;
    in: string;
    required?: boolean;
    schema?: OpenApiSchema;
    description?: string;
  }>;
  requestBody?: {
    content?: Record<
      string,
      {
        schema?: OpenApiSchema;
        examples?: Record<string, { summary?: string; value?: unknown }>;
      }
    >;
  };
};

type OpenApiSchema = Record<string, unknown> & {
  type?: string | string[];
  properties?: Record<string, OpenApiSchema>;
  required?: string[];
  enum?: unknown[];
  $ref?: string;
  example?: unknown;
  description?: string;
  format?: string;
  items?: OpenApiSchema;
  default?: unknown;
};

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;
type HttpMethod = (typeof HTTP_METHODS)[number];

const METHOD_COLORS: Record<HttpMethod, string> = {
  get: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  post: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  put: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  patch: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  delete: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

const normalizeApiBase = (): string => {
  const envBase = String(env.REACT_APP_API_URL || '').trim();
  const fallback = typeof window !== 'undefined' ? window.location.origin : '';
  return (envBase || fallback).replace(/\/+$/, '');
};

const copyToClipboard = (value: string, successLabel: string) => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    toolToast.error('Clipboard nao disponivel neste contexto');
    return;
  }
  navigator.clipboard
    .writeText(value)
    .then(() => toolToast.success(successLabel))
    .catch(() => toolToast.error('Falha ao copiar'));
};

type EntityRow = {
  entityName: string;
  fields: Array<{
    name: string;
    type: string;
    required: boolean;
    description?: string;
  }>;
  exampleValue: Record<string, unknown> | undefined;
};

const readableType = (schema: OpenApiSchema | undefined): string => {
  if (!schema) return 'any';
  if (schema.enum && Array.isArray(schema.enum) && schema.enum.length > 0) {
    return `enum(${schema.enum.map(String).join(' | ')})`;
  }
  if (Array.isArray(schema.type)) {
    return schema.type.join(' | ');
  }
  if (schema.type === 'array' && schema.items) {
    return `array<${readableType(schema.items)}>`;
  }
  if (schema.format) {
    return `${schema.type}(${schema.format})`;
  }
  return String(schema.type || 'any');
};

const extractEntities = (document: OpenApiDocument | null): EntityRow[] => {
  if (!document) return [];
  const schemas = document.components?.schemas ?? {};
  const entities: EntityRow[] = [];

  Object.entries(schemas).forEach(([name, schema]) => {
    if (!name.startsWith('EntityPayload_')) return;
    const entityName = name.replace(/^EntityPayload_/, '');
    const properties = schema.properties ?? {};
    const required = new Set<string>(schema.required ?? []);
    const fields = Object.entries(properties).map(([fieldName, fieldSchema]) => ({
      name: fieldName,
      type: readableType(fieldSchema),
      required: required.has(fieldName),
      description: typeof fieldSchema.description === 'string' ? fieldSchema.description : undefined,
    }));

    const createSchemaKey = `RecordCreate_${entityName}`;
    const createSchema = schemas[createSchemaKey];
    const exampleValue =
      createSchema &&
      typeof createSchema.example === 'object' &&
      createSchema.example !== null
        ? (createSchema.example as Record<string, unknown>)
        : undefined;

    entities.push({
      entityName,
      fields,
      exampleValue,
    });
  });

  entities.sort((a, b) => a.entityName.localeCompare(b.entityName));
  return entities;
};

type EndpointRow = {
  method: HttpMethod;
  path: string;
  summary: string;
  description: string;
  operation: OpenApiOperation;
};

const extractEndpoints = (document: OpenApiDocument | null): EndpointRow[] => {
  if (!document?.paths) return [];
  const rows: EndpointRow[] = [];
  Object.entries(document.paths).forEach(([path, methods]) => {
    HTTP_METHODS.forEach(method => {
      const operation = methods[method];
      if (!operation) return;
      rows.push({
        method,
        path,
        summary: String(operation.summary || ''),
        description: String(operation.description || ''),
        operation,
      });
    });
  });
  return rows;
};

const buildCurlForCreate = (
  baseUrl: string,
  path: string,
  exampleBody: Record<string, unknown> | undefined
): string => {
  const url = `${baseUrl}${path}`;
  const lines = [
    `curl -X POST '${url}' \\`,
    `  -H 'X-API-Key: <SUA_CHAVE>' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '${JSON.stringify(exampleBody ?? {}, null, 2)}'`,
  ];
  return lines.join('\n');
};

const buildCurlForOperation = (
  baseUrl: string,
  endpoint: EndpointRow,
  exampleBody?: Record<string, unknown>
): string => {
  const url = `${baseUrl}${endpoint.path.replace(/{record_id}/g, '<RECORD_ID>').replace(/{workflow_id}/g, '<WORKFLOW_ID>')}`;
  const methodUpper = endpoint.method.toUpperCase();
  const lines = [`curl -X ${methodUpper} '${url}' \\`, `  -H 'X-API-Key: <SUA_CHAVE>'`];
  const requiresBody = endpoint.method === 'post' || endpoint.method === 'patch' || endpoint.method === 'put';
  if (requiresBody && exampleBody) {
    lines.push(`  -H 'Content-Type: application/json' \\`);
    lines[lines.length - 2] = lines[lines.length - 2] + ' \\';
    lines.push(`  -d '${JSON.stringify(exampleBody, null, 2)}'`);
  }
  return lines.join('\n');
};

const CopyableField: React.FC<{ value: string; label: string; successLabel: string }> = ({
  value,
  label,
  successLabel,
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
    <div className="flex items-center gap-2">
      <code className="flex-1 truncate rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
        {value}
      </code>
      <button
        type="button"
        onClick={() => copyToClipboard(value, successLabel)}
        className="rounded border border-gray-200 bg-white p-1 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        aria-label="Copiar"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);

export const ApiDocsPanel: React.FC<ApiDocsPanelProps> = ({ toolId }) => {
  const { t } = useTranslation();
  const [document, setDocument] = useState<OpenApiDocument | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expandedEntity, setExpandedEntity] = useState<string | null>(null);
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);

  const baseUrl = useMemo(() => normalizeApiBase(), []);

  const load = useCallback(async () => {
    if (!toolId) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await toolService.getOpenApi(toolId);
      setDocument(response.openapi as OpenApiDocument);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('tools.apiDocs.loadError');
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [toolId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const entities = useMemo(() => extractEntities(document), [document]);
  const endpoints = useMemo(() => extractEndpoints(document), [document]);
  const createEndpoint = endpoints.find(
    e => e.method === 'post' && e.path.endsWith('/records')
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
        <span className="ml-2 text-sm text-gray-500">{t('tools.apiDocs.loading')}</span>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="space-y-3 p-4 text-left">
        <p className="text-sm text-rose-600 dark:text-rose-400">{errorMessage}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded border border-gray-200 px-3 py-1 text-xs hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          {t('tools.apiDocs.retry')}
        </button>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
        {t('tools.apiDocs.empty')}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto p-4 text-left">
      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('tools.apiDocs.howToIntegrate.title')}
        </h3>
        <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">
          {t('tools.apiDocs.howToIntegrate.description')}
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <CopyableField
            value={baseUrl}
            label={t('tools.apiDocs.howToIntegrate.baseUrl')}
            successLabel={t('tools.apiDocs.howToIntegrate.baseUrlCopied')}
          />
          <CopyableField
            value="X-API-Key: <SUA_CHAVE>"
            label={t('tools.apiDocs.howToIntegrate.authHeader')}
            successLabel={t('tools.apiDocs.howToIntegrate.authHeaderCopied')}
          />
        </div>
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          {t('tools.apiDocs.howToIntegrate.scopeWarning')}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('tools.apiDocs.entities.title')}
        </h3>
        {entities.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('tools.apiDocs.entities.empty')}
          </p>
        ) : (
          <ul className="space-y-2">
            {entities.map(entity => {
              const isOpen = expandedEntity === entity.entityName;
              const curlExample = buildCurlForCreate(
                baseUrl,
                createEndpoint?.path || '/api/v1/apps/<TOOL_ID>/records',
                entity.exampleValue
              );
              return (
                <li
                  key={entity.entityName}
                  className="rounded border border-gray-200 dark:border-gray-700"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedEntity(prev =>
                        prev === entity.entityName ? null : entity.entityName
                      )
                    }
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <span className="flex items-center gap-2">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                      <code className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {entity.entityName}
                      </code>
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('tools.apiDocs.entities.fieldCount', { count: entity.fields.length })}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-gray-200 p-3 dark:border-gray-700">
                      {entity.fields.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('tools.apiDocs.entities.noFields')}
                        </p>
                      ) : (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700 dark:text-gray-400">
                              <th className="py-1 pr-2 font-medium">
                                {t('tools.apiDocs.entities.field')}
                              </th>
                              <th className="py-1 pr-2 font-medium">
                                {t('tools.apiDocs.entities.type')}
                              </th>
                              <th className="py-1 font-medium">
                                {t('tools.apiDocs.entities.required')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="text-gray-800 dark:text-gray-100">
                            {entity.fields.map(field => (
                              <tr
                                key={field.name}
                                className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/60"
                              >
                                <td className="py-1 pr-2">
                                  <code>{field.name}</code>
                                  {field.description && (
                                    <div className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                                      {field.description}
                                    </div>
                                  )}
                                </td>
                                <td className="py-1 pr-2">
                                  <code className="text-blue-700 dark:text-blue-300">
                                    {field.type}
                                  </code>
                                </td>
                                <td className="py-1">
                                  {field.required ? (
                                    <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                                      {t('tools.apiDocs.entities.required')}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                            {t('tools.apiDocs.entities.exampleCreate')}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              copyToClipboard(
                                curlExample,
                                t('tools.apiDocs.entities.curlCopied')
                              )
                            }
                            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <Copy className="h-3 w-3" />
                            {t('tools.apiDocs.entities.copyCurl')}
                          </button>
                        </div>
                        <pre className="overflow-x-auto rounded bg-gray-900 p-2 text-[11px] leading-snug text-gray-100">
{curlExample}
                        </pre>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('tools.apiDocs.endpoints.title')}
        </h3>
        {endpoints.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('tools.apiDocs.endpoints.empty')}
          </p>
        ) : (
          <ul className="space-y-2">
            {endpoints.map(endpoint => {
              const id = `${endpoint.method}:${endpoint.path}`;
              const isOpen = expandedEndpoint === id;
              const exampleBody =
                endpoint.method === 'post' && endpoint.path.endsWith('/records')
                  ? entities[0]?.exampleValue
                  : undefined;
              const curl = buildCurlForOperation(baseUrl, endpoint, exampleBody);
              return (
                <li
                  key={id}
                  className="rounded border border-gray-200 dark:border-gray-700"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedEndpoint(prev => (prev === id ? null : id))}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <span
                      className={`min-w-[60px] rounded px-2 py-0.5 text-center text-[10px] font-bold uppercase ${METHOD_COLORS[endpoint.method]}`}
                    >
                      {endpoint.method}
                    </span>
                    <code className="flex-1 truncate text-xs text-gray-800 dark:text-gray-100">
                      {endpoint.path}
                    </code>
                    <span className="hidden text-xs text-gray-500 md:inline">
                      {endpoint.summary}
                    </span>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="space-y-3 border-t border-gray-200 p-3 text-xs dark:border-gray-700">
                      {endpoint.description && (
                        <p className="text-gray-600 dark:text-gray-300">{endpoint.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wide text-gray-500">
                          curl
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(curl, t('tools.apiDocs.endpoints.curlCopied'))
                          }
                          className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          <Copy className="h-3 w-3" />
                          {t('tools.apiDocs.endpoints.copy')}
                        </button>
                      </div>
                      <pre className="overflow-x-auto rounded bg-gray-900 p-2 text-[11px] leading-snug text-gray-100">
{curl}
                      </pre>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};
